import { parseOptionalInteger } from "@/utils/numberUtils";

export interface InvoiceAmountFields {
  amount?: number;
  vatType?: number;
  vatAmount?: number;
  withholdingType?: number;
  withholdingAmount?: number;
  totalAmount?: number;
  bankId?: string;
}

export interface ClientInvoiceDefaults {
  vatType?: number;
  withholding?: number;
  bankId?: string;
}

export interface InvoiceTaxTypeFields {
  vatType?: number | string | null;
  withholdingType?: number | string | null;
}

export interface InvoiceDeliveryNetTotal {
  netAmount: number;
  expectedTotalAmount: number;
}

export interface ComputeInvoiceDerivedAmountsOptions {
  force?: boolean;
  preserveTotalAmount?: boolean;
}

export function normalizeInvoiceTaxTypeFields(form: InvoiceTaxTypeFields): void {
  form.vatType = parseOptionalInteger(form.vatType);
  form.withholdingType = parseOptionalInteger(form.withholdingType);
}

export function normalizeInvoiceOptionalTextField(value?: string | null): string | undefined {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return undefined;
  }

  return trimmedValue;
}

function parseInvoiceAmount(value: unknown): number | undefined {
  if (value === "" || value == null) {
    return undefined;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  return parsed;
}

export function normalizeInvoiceAmountFromDelivery(form: InvoiceAmountFields): void {
  const amount = parseInvoiceAmount(form.amount);

  if (amount != null && amount !== 0) {
    form.amount = amount;
    return;
  }

  const totalAmount = parseInvoiceAmount(form.totalAmount);

  if (totalAmount == null) {
    return;
  }

  form.totalAmount = totalAmount;

  const vatAmount = parseInvoiceAmount(form.vatAmount);
  const withholdingAmount = parseInvoiceAmount(form.withholdingAmount);

  const hasNonZeroTaxBreakdown =
    (vatAmount != null && vatAmount !== 0) || (withholdingAmount != null && withholdingAmount !== 0);

  if (!hasNonZeroTaxBreakdown) {
    form.amount = totalAmount;
    return;
  }

  const derivedNetAmount = roundCurrency(totalAmount - (vatAmount ?? 0) + (withholdingAmount ?? 0));

  if (derivedNetAmount !== 0) {
    form.amount = derivedNetAmount;
  }
}

export function applyClientDefaultsToInvoiceForm(
  form: InvoiceAmountFields,
  client: ClientInvoiceDefaults | null | undefined
): void {
  if (!client) {
    return;
  }

  if (form.vatType == null && client.vatType != null) {
    form.vatType = client.vatType;
  }

  if (form.withholdingType == null && client.withholding != null) {
    form.withholdingType = client.withholding;
  }

  if (form.bankId == null && client.bankId != null) {
    form.bankId = client.bankId;
  }
}

export function getInvoiceDeliveryNetTotal(form: InvoiceAmountFields): InvoiceDeliveryNetTotal | undefined {
  const netAmount = parseInvoiceAmount(form.amount);

  if (netAmount == null) {
    return undefined;
  }

  const vatAmount = parseInvoiceAmount(form.vatAmount) ?? 0;
  const withholdingAmount = parseInvoiceAmount(form.withholdingAmount) ?? 0;

  return {
    netAmount,
    expectedTotalAmount: roundCurrency(netAmount + vatAmount - withholdingAmount),
  };
}

export function hasInvoiceTotalDeliveryMismatch(form: InvoiceAmountFields): boolean {
  const deliveryNetTotal = getInvoiceDeliveryNetTotal(form);
  const totalAmount = parseInvoiceAmount(form.totalAmount);

  if (!deliveryNetTotal || totalAmount == null) {
    return false;
  }

  return roundCurrency(totalAmount) !== deliveryNetTotal.expectedTotalAmount;
}

export function buildInvoiceTotalDeliveryMismatchMessage(
  deliveryNetTotal: InvoiceDeliveryNetTotal,
  formatCurrency: (amount: number) => string
): string {
  const netAmount = formatCurrency(deliveryNetTotal.netAmount);
  const expectedTotalAmount = formatCurrency(deliveryNetTotal.expectedTotalAmount);
  const mismatchNotice = "El importe total no coincide con el calculado a partir del importe neto de la entrega.";
  const expectedTotalNotice = `El importe neto es ${netAmount}, por tanto se esperaba que el importe total fuera ${expectedTotalAmount}.`;

  return `${mismatchNotice} ${expectedTotalNotice}`;
}

export function computeInvoiceDerivedAmounts(
  form: InvoiceAmountFields,
  options: ComputeInvoiceDerivedAmountsOptions = {}
): void {
  const { force = false, preserveTotalAmount = false } = options;
  const preservedTotalAmount = preserveTotalAmount ? parseInvoiceAmount(form.totalAmount) : undefined;

  const amount = parseInvoiceAmount(form.amount);

  if (amount == null) {
    return;
  }

  form.amount = amount;

  if (form.vatType != null && (force || form.vatAmount == null)) {
    form.vatAmount = roundCurrency(amount * (Number(form.vatType) / 100));
  }

  if (form.withholdingType != null && (force || form.withholdingAmount == null)) {
    form.withholdingAmount = roundCurrency(amount * (Number(form.withholdingType) / 100));
  }

  const vatAmount = parseInvoiceAmount(form.vatAmount) ?? 0;
  const withholdingAmount = parseInvoiceAmount(form.withholdingAmount) ?? 0;

  if (form.vatAmount != null) {
    form.vatAmount = vatAmount;
  }

  if (form.withholdingAmount != null) {
    form.withholdingAmount = withholdingAmount;
  }

  const calculatedTotal = roundCurrency(amount + vatAmount - withholdingAmount);
  // Invoices are born from deliveries with totalAmount equal to the net amount, so only a
  // different value proves the total was edited manually and must be preserved.
  const hasManualTotalAmount = preservedTotalAmount != null && roundCurrency(preservedTotalAmount) !== amount;

  if (preserveTotalAmount && hasManualTotalAmount) {
    form.totalAmount = preservedTotalAmount;
    return;
  }

  if (force || form.totalAmount == null) {
    form.totalAmount = calculatedTotal;
    return;
  }

  const totalAmount = parseInvoiceAmount(form.totalAmount);

  if (totalAmount === amount) {
    form.totalAmount = calculatedTotal;
  }
}

export function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getInvoiceWithholdingDisplayAmount(amount?: number | null): number | undefined {
  if (amount == null || amount === 0) {
    return amount ?? undefined;
  }

  return -Math.abs(amount);
}
